import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import apiService from "../services/ApiService";

// โมดูลกลางของเอกสารใบจองวัคซีน ใช้ร่วมกันระหว่างหน้าของเกษตรกร (VaccineReservationForm)
// และหน้าของเจ้าหน้าที่ (StaffVaccineReservationDetail) เพื่อให้เอกสารที่พิมพ์ ที่ดาวน์โหลด
// และที่ส่งไปกับอีเมล (app/services/reservationpdf.js ฝั่ง backend) เป็นรูปแบบเดียวกัน

// ใช้ THSarabunNew เพราะรองรับการวางตำแหน่งวรรณยุกต์/สระไทยใน PDF ได้ถูกต้อง
// (Sarabun-Regular ทำให้วรรณยุกต์พิมพ์ซ้อนกัน)
const THAI_FONT = "THSarabunNew";
const WATERMARK_LOGO_PATH = "/watermark-dldbiologic.png";

export const formatNumber = (value, options) =>
  Number(value || 0).toLocaleString("th-TH", options);

export const formatThaiDate = (value) => {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("th-TH");
};

// UpdatedAt เก็บเป็นเวลาท้องถิ่นแต่ถูกส่งมาพร้อม Z จึงต้องตัดออกก่อนแปลง
export const formatThaiDateTime = (value) => {
  if (!value) return "-";
  const normalized =
    typeof value === "string" && value.endsWith("Z") ? value.slice(0, -1) : value;
  const date = normalized instanceof Date ? normalized : new Date(normalized);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.toLocaleDateString("th-TH")} ${date.toLocaleTimeString("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

/**
 * สรุปเฉพาะฟิลด์ที่ปรากฏบนเอกสารใบจอง เพื่อเทียบว่าฟอร์มต่างจากข้อมูลที่บันทึกไว้หรือยัง
 * ใช้ตัดสินว่าอนุญาตให้พิมพ์เอกสารได้หรือไม่
 */
export const buildComparableState = (data) =>
  JSON.stringify({
    PickupPoint: data.PickupPoint || "",
    FarmerName: data.FarmerName || "",
    RefTaxID: data.RefTaxID || "",
    FarmerTel: data.FarmerTel || "",
    FarmerEmail: data.FarmerEmail || "",
    UsageObjective: data.UsageObjective || "",
    VaccineDetails: (data.VaccineDetails || [])
      .map((detail) => ({
        VaccineID: detail.VaccineID,
        RequestQuantity: Number(detail.RequestQuantity) || 0,
      }))
      // เรียงก่อนเทียบ เพื่อไม่ให้ลำดับการเพิ่ม/ลบทำให้ผลต่างกัน
      .sort((a, b) => String(a.VaccineID).localeCompare(String(b.VaccineID))),
  });

const loadFileAsDataUrl = async (path) => {
  try {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
    }
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error(`Error loading ${path}:`, error);
    return null;
  }
};

const loadThaiFont = async (fileName) => {
  const dataUrl = await loadFileAsDataUrl(`/fonts/${fileName}`);
  if (!dataUrl) return null;
  return dataUrl.substring(dataUrl.indexOf(",") + 1);
};

/** ลงทะเบียนฟอนต์ไทยทั้งแบบปกติและตัวหนาให้ jsPDF คืนค่าว่าใช้ฟอนต์ไทยได้หรือไม่ */
const registerThaiFont = async (doc) => {
  const [regular, bold] = await Promise.all([
    loadThaiFont("THSarabunNew.ttf"),
    loadThaiFont("THSarabunNew Bold.ttf"),
  ]);

  if (!regular) {
    console.warn("Failed to load Thai font, using default font");
    doc.setFont("helvetica");
    return false;
  }

  doc.addFileToVFS("THSarabunNew.ttf", regular);
  doc.addFont("THSarabunNew.ttf", THAI_FONT, "normal");

  // ถ้าไม่มีตัวหนา ให้ map bold ไปที่ไฟล์ปกติ เพื่อไม่ให้ jsPDF หาฟอนต์ไม่เจอ
  doc.addFileToVFS("THSarabunNew-Bold.ttf", bold || regular);
  doc.addFont("THSarabunNew-Bold.ttf", THAI_FONT, "bold");

  doc.setFont(THAI_FONT, "normal");
  return true;
};

/**
 * วางโลโก้เป็นลายน้ำกลางหน้าทุกหน้า
 * ต้องเรียกหลังสร้างตารางเสร็จ เพราะพื้นหลังสีขาวของตารางจะทับลายน้ำที่วาดไว้ก่อน
 */
const addPdfWatermark = (doc, logoDataUrl) => {
  if (!logoDataUrl) return;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const size = pageWidth * 0.45;
  const x = (pageWidth - size) / 2;
  const y = (pageHeight - size) / 2;
  const pageCount = doc.internal.getNumberOfPages();
  const supportsOpacity = typeof doc.GState === "function";

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    if (supportsOpacity) {
      doc.setGState(new doc.GState({ opacity: 0.08 }));
    }
    doc.addImage(logoDataUrl, "PNG", x, y, size, size);
    if (supportsOpacity) {
      doc.setGState(new doc.GState({ opacity: 1 }));
    }
  }
};

/**
 * ดึงข้อมูลใบจองจากฐานข้อมูลใหม่ทุกครั้งที่พิมพ์
 * เพื่อไม่ให้เอกสารที่พิมพ์ออกไปมีค่าที่ยังไม่ได้บันทึกจากในฟอร์ม
 */
export const fetchReservationForPrint = async ({
  reservationID,
  sessionId,
  vaccineOptions = [],
}) => {
  const response = await apiService({
    method: "POST",
    url: "/getVaccineReservationsByID",
    data: { ReservationID: reservationID },
    sessionId: sessionId || "",
  });

  if (response.result !== "success" || !response.VaccineReservation) {
    throw new Error(response.error || "ไม่พบข้อมูลใบจองในระบบ");
  }

  const reservation = response.VaccineReservation;
  const details = (response.VaccineReservationDetails || []).map((detail) => {
    const vaccine = vaccineOptions.find((v) => v.VaccineID === detail.VaccineID);
    const packageSize = vaccine?.PackageSize || 1;
    return {
      VaccineID: detail.VaccineID,
      name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
      RequestQuantity: detail.RequestQuantity,
      DoseCount: Math.ceil(detail.RequestQuantity * packageSize),
      UnitPrice: detail.UnitPrice,
      TotalPrice: detail.TotalPrice,
    };
  });

  return {
    reservation,
    details,
    totalQuantity: details.reduce(
      (sum, detail) => sum + Number(detail.RequestQuantity || 0),
      0
    ),
    totalPrice: details.reduce(
      (sum, detail) => sum + Number(detail.TotalPrice || 0),
      0
    ),
  };
};

/**
 * สร้างเอกสาร PDF ใบจอง ใช้ร่วมกันทั้งการพิมพ์และการดาวน์โหลดไฟล์
 * เพื่อให้เอกสารทั้งสองแบบมีรูปแบบเดียวกัน
 */
export const buildReservationDoc = async ({
  reservation,
  details,
  warehouseName,
  periodEndDate,
  totalQuantity,
  totalPrice,
}) => {
  const doc = new jsPDF();
  const hasThaiFont = await registerThaiFont(doc);
  const pdfFont = hasThaiFont ? THAI_FONT : "helvetica";
  const logoDataUrl = await loadFileAsDataUrl(WATERMARK_LOGO_PATH);

  // Header — THSarabunNew เล็กกว่า Sarabun ที่ขนาด pt เดียวกัน จึงต้องเพิ่มขนาด
  doc.setFontSize(18);
  doc.text("สำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์", 105, 20, { align: "center" });
  doc.text("ใบสั่งจองวัคซีนออนไลน์", 105, 30, { align: "center" });
  doc.setFontSize(14);
  doc.text(`เลขที่ใบจอง: ${reservation.ReservationID}`, 20, 40);
  doc.text(`วันที่สั่งจอง: ${formatThaiDate(reservation.ReservationDate)}`, 120, 40);
  doc.text(`ชื่อเกษตรกร: ${reservation.FarmerName || "-"}`, 20, 50);
  doc.text(`เบอร์โทร: ${reservation.FarmerTel || "-"}`, 20, 60);
  doc.text(`อีเมล: ${reservation.FarmerEmail || "-"}`, 120, 60);
  doc.text(`รอบการจัดส่ง: ${reservation.DeliveryPeriod || "-"}`, 20, 70);
  doc.text(`สถานะ: ${reservation.ReservationStatus || "-"}`, 120, 70);
  doc.text(`จุดรับวัคซีน: ${warehouseName}`, 20, 80);
  doc.text(`วันที่พิมพ์เอกสาร: ${formatThaiDateTime(new Date())}`, 120, 80);

  autoTable(doc, {
    startY: 90,
    head: [
      [
        "รหัสวัคซีน",
        "ชื่อวัคซีน",
        "จำนวน (ขวด)",
        "จำนวน (โดส)",
        "ราคา/ขวด (บาท)",
        "ราคารวม (บาท)",
      ],
    ],
    body: [
      ...details.map((detail) => [
        detail.VaccineID,
        detail.name,
        formatNumber(detail.RequestQuantity),
        formatNumber(detail.DoseCount),
        formatNumber(detail.UnitPrice, { minimumFractionDigits: 2 }),
        formatNumber(detail.TotalPrice, { minimumFractionDigits: 2 }),
      ]),
      [
        "",
        "ราคารวมทั้งสิ้น (บาท)",
        formatNumber(totalQuantity),
        "",
        "",
        formatNumber(totalPrice, { minimumFractionDigits: 2 }),
      ],
    ],
    theme: "grid",
    styles: {
      font: pdfFont,
      fontSize: 11,
      cellPadding: 2,
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
      fillColor: [255, 255, 255],
    },
    headStyles: {
      font: pdfFont,
      fontStyle: "normal",
      fillColor: [211, 211, 211],
      textColor: "black",
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: 20 },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
    },
    didParseCell: (data) => {
      if (
        data.row.section === "body" &&
        data.row.cells[1]?.content === "ราคารวมทั้งสิ้น (บาท)"
      ) {
        data.row.cells.forEach((cell) => {
          cell.styles.fontStyle = "bold";
          cell.styles.fillColor = [240, 240, 240];
        });
      }
    },
  });

  const finalY = doc.lastAutoTable.finalY || 34;
  doc.setFontSize(10);
  doc.text(
    [
      "หมายเหตุ : เอกสารนี้เป็นเอกสารการสั่งจองวัคซีนออนไลน์ ที่จัดทำขึ้นโดยระบบอัตโนมัติ ของสำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์",
      "1. เอกสารนี้ยังไม่ใช่การยืนยันการรับจองวัคซีน เจ้าหน้าที่จะตรวจสอบ และยืนยันการจองอีกครั้ง",
      "2. รายการจองวัคซีนนี้จะถูกจัดส่งตามรอบการจัดส่งที่เลือกไว้",
      `3. กรุณาตรวจสอบข้อมูลการจองวัคซีนให้ถูกต้อง โดยท่านสามารถแก้ไขข้อมูลได้ภายในวันที่กำหนด (${periodEndDate})`,
      `4. หากท่านต้องการแก้ไขรายการจอง หรือยกเลิกการจองวัคซีนหลังจากวันที่ ${periodEndDate} กรุณาติดต่อเจ้าหน้าที่สำนักงานปศุสัตว์จังหวัดที่ท่านระบุเป็นจุดรับวัคซีน`,
      "5. หากท่านต้องการสอบถามข้อมูลเพิ่มเติม กรุณาติดต่อเจ้าหน้าที่สำนักงานปศุสัตว์จังหวัด หรือสำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์",
    ],
    14,
    finalY + 5
  );

  addPdfWatermark(doc, logoDataUrl);
  return doc;
};

/**
 * พิมพ์จากไฟล์ PDF โดยตรง เพื่อให้เอกสารที่พิมพ์มีรูปแบบเดียวกับไฟล์ที่ดาวน์โหลด
 * คืนค่า false เมื่อเบราว์เซอร์บล็อกการเปิดหน้าต่างใหม่
 */
export const printReservationDoc = (doc) => {
  doc.autoPrint({ variant: "non-conform" });
  return Boolean(window.open(doc.output("bloburl"), "_blank"));
};
